import React, { useEffect, useRef, useState } from 'react';

// Generic carousel that shows a sliding window of items
// Parent provides the leftmost visible index via startIndex and the visible count via pageSize
const GAP_PX = 16; // Tailwind gap-4 default

const ExploreCarousel = ({ items = [], startIndex = 0, pageSize = 4, className = '', itemHeight = 280 }) => {
  const effectiveCount = Math.max(1, pageSize || 1);
  const total = Array.isArray(items) ? items.length : 0;
  const maxStart = Math.max(0, total - effectiveCount);
  const start = Math.min(Math.max(0, startIndex || 0), maxStart);
  const containerRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [itemWidthPx, setItemWidthPx] = useState(0);
  const [offsetPx, setOffsetPx] = useState(0);
  const prevStartRef = useRef(start);
  const [internalStart, setInternalStart] = useState(start);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState(0); // 1 = next, -1 = prev
  useEffect(() => {
    const prev = prevStartRef.current;
    if (start !== prev) {
      const delta = Math.abs(start - prev);
      if (delta > 1) {
        // Snap immediately without animation when jumping multiple slots (e.g., during tours)
        setInternalStart(start);
        setOffsetPx(0);
        setAnimating(false);
      } else {
        const dir = start > prev ? 1 : -1;
        const baseStart = dir === 1 ? prev : start;
        setInternalStart(baseStart);
        setDirection(dir);
        setAnimating(true);
        // after paint, animate by one slot
        requestAnimationFrame(() => {
          setOffsetPx(Math.max(0, itemWidthPx + GAP_PX) * (dir === 1 ? 1 : -1));
        });
        // after animation, snap to new window
        setTimeout(() => {
          setInternalStart(start);
          setOffsetPx(0);
          setAnimating(false);
        }, 320);
      }
    }
    prevStartRef.current = start;
  }, [start, itemWidthPx]);

  useEffect(() => {
    const getUsableWidth = (node) => {
      try {
        const vp = viewportRef.current;
        if (vp) {
          const rectVp = vp.getBoundingClientRect();
          if (rectVp && rectVp.width) return Math.floor(rectVp.width);
        }
        let el = node;
        while (el && el !== document.body) {
          const rect = el.getBoundingClientRect && el.getBoundingClientRect();
          const w = rect && rect.width ? rect.width : el.clientWidth;
          if (w && w > 0) return Math.floor(w);
          el = el.parentElement;
        }
        return typeof window !== 'undefined' ? Math.floor(window.innerWidth || 0) : 0;
      } catch (_) { return 0; }
    };

    const measure = () => {
      try {
        const node = containerRef.current;
        const cw = getUsableWidth(node);
        const count = Math.max(1, pageSize || 1);
        const k = Math.min(Math.max(0, startIndex || 0), Math.max(0, (Array.isArray(items) ? items.length : 0) - count));
        const gap = GAP_PX;
        const vw = cw > 0 ? Math.max(0, Math.floor(cw)) : 0;
        const slotWidth = vw > 0 ? Math.max(0, Math.floor((vw - gap * (count - 1)) / count)) : 0;
        setItemWidthPx(slotWidth);
        if (!animating) setOffsetPx(0);
      } catch (_) {}
    };
    // Defer to next frame to ensure layout complete
    requestAnimationFrame(measure);
    try { window.addEventListener('resize', measure, { passive: true }); } catch (_) {}
    return () => { try { window.removeEventListener('resize', measure); } catch (_) {} };
  }, [items, pageSize, startIndex]);

  return (
    <div
      className={`w-full overflow-hidden ${className}`}
      style={{
        height: itemHeight,
        ['--count']: effectiveCount,
        ['--gap']: `${GAP_PX}px`,
        ['--k']: start,
        ['--itemWidth']: `calc((100% - (var(--gap) * (var(--count) - 1))) / var(--count))`,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative'
      }}
    >
      <div ref={viewportRef} data-carousel-viewport="1" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', height: '100%', boxSizing: 'border-box', position: 'relative', contain: 'layout paint size' }}>
        <div
          ref={trackRef}
          data-carousel-track="1"
          className="h-full flex flex-row flex-nowrap"
          style={{ position: 'absolute', left: `${Math.round(-offsetPx)}px`, top: 0, columnGap: `${GAP_PX}px`, width: `${Math.max(0, Math.round((itemWidthPx + GAP_PX) * Math.min((items?.length || 0) - internalStart, animating ? (effectiveCount + 1) : effectiveCount) - GAP_PX))}px`, transition: animating ? 'left 300ms ease' : 'none' }}
        >
          {(() => {
            const windowSize = Math.min((items || []).length - internalStart, animating ? (effectiveCount + 1) : effectiveCount);
            const slice = (items || []).slice(internalStart, internalStart + windowSize);
            return slice.map((node, idx) => (
              <div
                key={`carousel-item-${internalStart + idx}`}
                className="min-h-[120px] h-full min-w-0 overflow-hidden"
                style={{ flex: `0 0 ${Math.max(0, Math.round(itemWidthPx))}px`, maxWidth: `${Math.max(0, Math.round(itemWidthPx))}px`, boxSizing: 'border-box' }}
              >
                <div className="h-full w-full">
                  {node}
                </div>
              </div>
            ))
          })()}
        </div>
      </div>
    </div>
  );
};

export default ExploreCarousel;




